import React from 'react';

interface StatCardProps {
    title: string;
    value: string;
    valueClassName?: string;
    // Optional small line under the value — e.g. a breakdown of the total
    // (Direct/Indirect, etc.) that doesn't belong in the headline number itself.
    subtitle?: React.ReactNode;
}

// Shared summary-card look used across every tab's stat row (label + big value,
// no icon chip) — matches Orders/OrdersTab.tsx, the reference layout other tabs
// were asked to align with. Always render inside the shared `StatCardRow`
// wrapper below so it gets the horizontal-scroll-on-mobile behavior.
const StatCard: React.FC<StatCardProps> = ({ title, value, valueClassName, subtitle }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-col gap-2 shadow-sm">
        <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">{title}</p>
        <p className={`text-lg sm:text-2xl font-black mt-1 tracking-tight whitespace-nowrap ${valueClassName || 'text-slate-800'}`}>{value}</p>
        {/* Unlike title/value (always short), a subtitle can be a longer
            breakdown (e.g. "Direct ₹X · Indirect ₹Y") -- nowrap would let it
            spill past the card's edge instead of wrapping onto a second line. */}
        {subtitle && <p className="text-[11px] font-semibold text-slate-500 break-words">{subtitle}</p>}
    </div>
);

export default StatCard;

interface StatCardRowProps {
    children: React.ReactNode;
    className?: string;
}

// Lays out stat cards in ONE row that scrolls sideways. Each card is as wide
// as its own content (label/value are `whitespace-nowrap`), with a 180px floor
// so short values like "683" still read as a card; cards never stretch to fill
// the row, never shrink below their content, and never wrap to a second row.
//
// History: a fixed 6-column grid made long amounts ("₹25,55,89,200") overflow
// their 1/6 slot; `flex-1` fixed that but stretched a lone card (e.g. only
// "Paid Units" for users without finance access) across the whole page.
export const StatCardRow: React.FC<StatCardRowProps> = ({ children, className = '' }) => (
    <div className={`flex flex-nowrap gap-3 sm:gap-4 mb-4 overflow-x-auto pb-1 [&>*]:shrink-0 [&>*]:grow-0 [&>*]:min-w-[180px] ${className}`}>
        {children}
    </div>
);
